import type {
  PlanningConfiguration,
  PlanningConfigurationRepository,
} from "../../../application/ports/planning-configuration";
import {
  DEFAULT_CALORIE_RECOMMENDATION_POLICY_V1,
  validateCalorieRecommendationPolicy,
} from "../../../domain/check-in/recommendation-policy";
import {
  calculateDailyCalorieFloor,
  DEFAULT_DAILY_FLOOR_POLICY_V1,
} from "../../../domain/planning/daily-floor-policy";
import { validateMacroDistribution } from "../../../domain/planning/macro-targets";
import type { DietTrackerDatabase } from "./database";
import { mutateDatabase } from "./mutate";

const METADATA_KEY = "planning-configuration";

export const DEFAULT_PLANNING_CONFIGURATION: PlanningConfiguration = Object.freeze({
  macroDistribution: Object.freeze({ proteinPercent: 30, carbohydratePercent: 40, fatPercent: 30 }),
  dailyFloorPolicy: DEFAULT_DAILY_FLOOR_POLICY_V1,
  recommendationPolicy: DEFAULT_CALORIE_RECOMMENDATION_POLICY_V1,
});

export class DexiePlanningConfigurationRepository implements PlanningConfigurationRepository {
  constructor(
    private readonly database: DietTrackerDatabase,
    private readonly defaults: PlanningConfiguration = DEFAULT_PLANNING_CONFIGURATION,
  ) {
    validateConfiguration(defaults);
  }

  async get(): Promise<PlanningConfiguration> {
    const stored = await this.database.metadata.get(METADATA_KEY);
    if (typeof stored?.value !== "string") return cloneConfiguration(this.defaults);
    try {
      const parsed = JSON.parse(stored.value) as PlanningConfiguration;
      validateConfiguration(parsed);
      return cloneConfiguration(parsed);
    } catch {
      // Corrupt settings must not prevent check-in; safe versioned defaults remain
      // available while the next save replaces the bad metadata value.
      return cloneConfiguration(this.defaults);
    }
  }

  async save(configuration: PlanningConfiguration): Promise<void> {
    validateConfiguration(configuration);
    await mutateDatabase(this.database, async () => {
      await this.database.metadata.put({ key: METADATA_KEY, value: JSON.stringify(configuration) });
    });
  }
}

function validateConfiguration(configuration: PlanningConfiguration): void {
  validateMacroDistribution(configuration.macroDistribution);
  calculateDailyCalorieFloor(configuration.dailyFloorPolicy, 0, configuration.macroDistribution);
  validateCalorieRecommendationPolicy(configuration.recommendationPolicy);
}

function cloneConfiguration(configuration: PlanningConfiguration): PlanningConfiguration {
  return {
    macroDistribution: { ...configuration.macroDistribution },
    dailyFloorPolicy: { ...configuration.dailyFloorPolicy },
    recommendationPolicy: { ...configuration.recommendationPolicy },
  };
}
